import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';

import { HttpMethod } from '@api/enums';
import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';
import { filterLabelIdsAtom } from '@store/atoms/filter-labelIds';

import { ASSET_INFO } from '../constants';
import { AssetType } from '../enums';
import {
  AssetListResponse,
  CreateBrandAssetRequest,
  CreateBrandAssetResponse,
} from '../types';

export const useCreateBrandAsset = () => {
  const client = createHttpClient();
  const queryClient = useQueryClient();
  const filterLabelIds = useRecoilValue(filterLabelIdsAtom);

  const mutation = useMutation(
    (payload: CreateBrandAssetRequest) =>
      client<CreateBrandAssetResponse>({
        method: HttpMethod.POST,
        url: END_POINTS.BRAND_LIBRARY.CREATE_ASSET,
        data: payload,
      }),
    {
      retry: 0,
      onSuccess: (data: CreateBrandAssetResponse) => {
        showAlert({
          message: `${ASSET_INFO[data.assetType as AssetType]?.name} added successfully`,
          type: AlertType.SUCCESS,
        });

        if (
          data?.assetType === AssetType.KNOWLEDGE ||
          data?.assetType === AssetType?.RAW_FILE
        ) {
        }
        queryClient.refetchQueries(QUERY_KEYS.HIERARCHY.ASSETS_HIERARCHY);
        if (filterLabelIds && filterLabelIds.length > 0) {
          // If there are labelIds, invalidate the cache
          queryClient.invalidateQueries([
            QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST,
            filterLabelIds,
          ]);
        } else {
          // Else update cache
          const assetListCache = queryClient.getQueryData<
            InfiniteData<AssetListResponse>
          >([QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST, filterLabelIds]);
          assetListCache?.pages[0].items.unshift(data);

          queryClient.setQueryData(
            [QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST, filterLabelIds],
            {
              ...assetListCache,
            },
          );
        }
      },
      onError: (error: Error) => {
        showAlert({
          type: AlertType.ERROR,
          message: `${error?.message || 'Request failed'}`,
          header: 'Failed to create asset',
        });
      },
    },
  );

  return mutation;
};
